import React from "react";
import { Box, Button, Spinner, Text } from "@chakra-ui/react";
import { CreateAttemptButton } from "./CreateAttemptButton";
import { Link as GatsbyLink, withPrefix } from "gatsby";
import { UserButtons as LoginButton } from "@components/UserStatus/UserButtons";
import {
  formatDate,
  getExamRetakeDate,
  isAttemptExpired,
  useUser,
} from "@utils";

type ExamButtonProps = {
  testId: string;
  examSlug: string;
  certificationId: string;
};

export function ExamButton({
  testId,
  examSlug,
  certificationId,
}: ExamButtonProps) {
  const { user, loading, error } = useUser();

  if (loading) {
    return <Spinner size="lg" />;
  }

  if (error) {
    return <Text>{error.message}</Text>;
  }

  if (!user) {
    return <LoginButton showSingleButton />;
  }

  const certification = user.certifications?.find(
    (cert) => cert.certificationId === certificationId
  );

  // does the user have this certification already?
  if (certification) {
    return (
      <Button
        colorScheme="indigo"
        as="a"
        href={withPrefix(`/certifications/${certification.id}`)}
      >
        View certification
      </Button>
    );
  }

  const userAttempts = user.attempts ?? [];
  const [recentAttempt] = userAttempts
    .filter((attempt) => attempt.testId === testId)
    .sort(
      (a, b) =>
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );

  // has the user taken this exam before?
  if (recentAttempt) {
    const startedDate = new Date(recentAttempt.startedAt);
    const retakeDate = getExamRetakeDate(startedDate);

    // are we within the retake waiting window?
    // if no skip to the end and render start button
    if (new Date() <= retakeDate) {
      const isExpired = isAttemptExpired(startedDate);

      // has the exam been completed or has time expired?
      if (recentAttempt.completedAt || isExpired) {
        return (
          <Box w="220px" fontStyle="italic" textAlign="center">
            You will be eligible to retake the exam on {formatDate(retakeDate)}.
          </Box>
        );
      }

      // not expired or completed, resume exam
      return (
        <Button
          colorScheme="indigo"
          as={GatsbyLink}
          to={`/exams/${examSlug}/${recentAttempt.id}`}
        >
          Resume exam
        </Button>
      );
    }
  }

  return (
    <CreateAttemptButton
      examSlug={examSlug}
      variables={{
        userId: user.id,
        testId,
      }}
    />
  );
}
